<template>
  <main class="app-get-step" id="mainContent" ref="mainContentRef">
    <div class="app-main-content">
      <div>
        <form>
            <div class="transaction-total">
                <button type="button" class="add-button" @click="addAnotherTrxn()">
                Add another transaction
                </button>
                <span class="sub-total">
                <strong>
                    Subtotal ({{ store.state.guestLogin.cartTransactions?.cartTransactions?.length }} transaction(s)):
                    {{ toCurrencyNum(subTotal) }}
                </strong>
                </span>
            </div>
            <ul class="transaction-table" v-if="store.state.guestLogin.renewalCart?.length > 0">
                <li class="transaction-view" v-for="(transactionCart, index) in store.state.guestLogin.renewalCart" :key="index">
                    <div class="app-reg-notice">
                        <p class="vehicle-reg-info">
                            <strong>{{ transactionCart.description }} - </strong>
                            {{ transactionCart.year }} {{ transactionCart.make }}
                            {{ transactionCart.model !== 'UNKNOWN' ? transactionCart.model : '' }}
                            {{ transactionCart.plateNumber }} 
                            <template v-if="transactionCart.plateNumber || transactionCart.plateType"> / {{ transactionCart.plateType }}</template>
                        </p>
                    </div>
                    <div class="app-reg-notice">
                        <CustomButton class="add-button resend-code" @click="removeTrxn(index)">
                        Remove from cart
                        </CustomButton>
                    </div>
                
                    <div class="app-reg-notice total-fee" v-if="transactionCart.onlineTransactionFeeCollection?.length > 0">
                        <div 
                        class="fees" v-for="(txnFeesCart, i) in transactionCart.onlineTransactionFeeCollection"
                        :key="i"
                        >
                        <div class="fees-list">
                            <p>{{ txnFeesCart.feeName }}</p>
                            <p class="fee-amount">{{ toCurrencyNum(txnFeesCart.feeAmount) }}</p>
                        </div>
                        </div>
                        <div class="fees">
                        <div class="fees-list">
                            <p><strong>Total</strong></p>
                            <p class="total-charge"><strong>{{ toCurrencyNum(totalAmount(transactionCart)) }}</strong></p>
                        </div>
                        </div>
                    </div>
                    <div class="app-reg-notice total-fee" v-else>
                                    
                        <div class="fees">
                            <div class="fees-list">
                                <p><strong>Total</strong> </p> <p class="total-charge"><strong> {{ toCurrencyNum(totalAmount(transactionCart)) }}</strong></p>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
            <p class="app-error-message" v-if="transactionError" ref="transactionErrorRef" tabindex="-1"> You cannot add more than {{ MAX_TRANSACTIONS }} transaction(s) into the cart. Please remove a transaction before adding a new one or proceed to checkout.</p>
            <p id="app-error-message" class="sr-only" v-if="transactionError"> You cannot add more than {{ MAX_TRANSACTIONS }} transaction(s) into the cart. Please remove a transaction before adding a new one or proceed to checkout.</p>          
            <BaseButtonGroup>
                <button type="button" class="add-button" @click="addAnotherTrxn()"> Add another transaction </button>
                <button type="button" class="app-btn-primary" @click="handleRouting(false)"> Proceed to checkout </button>
            </BaseButtonGroup>
        </form>
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import * as COMMONS from '../../store/_commons';
import { toCurrency } from '@/utils/helpers';
import CustomButton from '../../components/CustomButton.vue';

// Use Vuex, Vue Router, and emits
const store = useStore();
const router = useRouter();
const emit = defineEmits(['getStep']);

// Reactive state
const stepNum = ref(3);
const screenData = ref({});
const mainContentRef = ref(null);

const transactionError = ref(false);
const txnCount = computed(() => store.state.guestLogin.cartTransactions?.cartTransactions?.length || 0);
const MAX_TRANSACTIONS = ref(window.config.MAX_TRANSACTIONS);
const transactionErrorRef = ref(null);

// Computed properties
const STEPS = computed(() => store.state.STEPS);
const isBusinessUser = computed(() => store.state.guestLogin.isBusinessUser);
const profileRouteName = computed(() => {
  return isBusinessUser.value ? 'BusinessProfile' : 'Profile';
});

const subTotal = computed(() => {
  let total = 0;
  if (store.state.guestLogin.renewalCart?.length > 0) {
    store.state.guestLogin.renewalCart.forEach(obj => {
      if (obj.onlineTransactionFeeCollection) {
        obj.onlineTransactionFeeCollection.forEach(txn => {
          total += txn.feeAmount;
        });
      }
    });
  }
  return total.toFixed(2);
});

// Methods
const toCurrencyNum = (value) => toCurrency(value);

const handleRouting = async (isPrev = false) => {
  if (isPrev) {
    emit('getStep', 3);
    store.state.guestLogin.step = 3;
  } else {
    store.state.guestLogin.cartTotal = subTotal.value;
    if (subTotal.value > 0) {
      await store.dispatch(COMMONS.GUEST_SERVICEFEES, subTotal.value);
    } 
    else {
      store.state.guestLogin.totalBill = null;
      store.state.guestLogin.step = 5;
    }
  }
};

const addAnotherTrxn = () => {
  if (txnCount.value < MAX_TRANSACTIONS.value) {
    transactionError.value = false;
    store.state.guestLogin.step = 1;
    router.push({ name: profileRouteName.value });
  } else {
    transactionError.value = true;
    nextTick(() => {
      if (transactionErrorRef.value) {
        transactionErrorRef.value.focus();
      }
    });
  }
};

const removeTrxn = async (step) => {
  try {
    store.commit('setState', { property: 'isLoading', value: true });
    await store.dispatch(COMMONS.GUEST_REMOVETRANSACTION, step);

    if ((store.state.guestLogin.renewalCart?.length || 0) < window.config.MAX_TRANSACTIONS) {
      transactionError.value = false;
    }
  } catch (error) {
    console.error(error);
  } finally {
    store.commit('setState', { property: 'isLoading', value: false });
  }
};

const totalAmount = (txnFeesCart) => {
  let total = 0;
  if (txnFeesCart.onlineTransactionFeeCollection) {
    txnFeesCart.onlineTransactionFeeCollection.forEach(obj => {
      total += obj.feeAmount;
    });
  }
  return total;
};

// Lifecycle hooks
onMounted(() => {
  window.scroll(0, 0);
  screenData.value = STEPS.value[stepNum.value];

  document.body.setAttribute("tabindex", "1");
  document.body.focus();
  document.body.removeAttribute("tabindex");

  nextTick(() => {
    if (mainContentRef.value) {
      mainContentRef.value.focus({ preventScroll: true });
    }
  });
});
</script>


<style scoped lang="scss">
.app-alert-error {
    display: block;
    margin-top: 20px;
    margin-bottom: 20px;
    font-size: 18px;
    line-height: 28px;
    padding-left: 10px;
    padding-right: 10px;
}
.transaction-total {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 2rem;
    margin-top: 1rem;
    p{
        width: 14%;
    }
    .sub-total {
        text-align: right;
        font-size: 20px;
    }
    .add-button {
        margin-top: 1rem;
    }
}
.transaction-table {
    li {
        background-color: white;
        &:nth-child(odd) {
            background-color: #F2F2F2;
        }
    }
    .transaction-view {
        display: flex;
        margin-bottom: 2rem;
        justify-content: space-between;
        .app-reg-notice {
            height: 100%;
            margin-bottom: 0px;
            margin-top: 5px;
            padding: 1rem;
            width: 100%;
            .resend-code{
                background-color: white;
                color: var(--ri-blue);
                &:hover{
                    background-color: var(--ri-lighter-blue);
                    color: var(--ri-darker-blue);
                }
            }
        }
        .white-space{
            position: relative;
        }
        @media only screen and (max-width: 796px) {
            .app-reg-notice {
                display: block; 
                margin-top: 5px;
            }
        }
        @media only screen and (max-width: 956px){
            .app-reg-notice-vehicle{
                margin-left: 16px; 
            }
        }
        @media only screen and (min-width: 957px) {
            .app-reg-notice-vehicle{
                max-width: 100%;
                height: 100%;
                margin-bottom: 0px;
                margin-top: 5px;
                padding: 1rem;
                position: relative; 
            }
        }
        .fees {
            background-color: #1B243B;
            color: white;
            .fees-list{
                display: flex;
                justify-content: space-between;
                gap: 1rem;
                p {
                    font-size: 16px;
                    line-height: 23px;
                    padding: 10px;
                }
                .total-charge{
                    color: #FFA500;
                    font-weight: 700;
                    width: 100px;
                    text-align: right;
                }
                .fee-amount {
                    text-align: right;
                    width: 100px;
                }
            }
        }
    }
}

.app-error-message {
  color: var(--ri-red);
  @extend %font-sans-regular;
  line-height: 1.35em;
  margin: unset;
  padding: 0.5em 0;
  &.--checker {
    margin-left: 1.5em;
  }
} 

button {
    margin-bottom: 1.125em;
    &:not(:last-child) {
    margin-right: 1em;
    }
    &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
    }
}

@media only screen and (max-width: 720px) {
    .transaction-total{
        display: block;
        p {
            display: none;
        }
        span {
            display: block;
            margin-top: 1rem;
            text-align: left !important;
        }
    }
    .transaction-table {
        display: block;
        .transaction-view {
            display: block;

            .white-space{
                width: unset;
                background-color: unset;
                top: 0px;
                position: relative;
                margin-left: 0rem;
            }
        }
    }
}

@media only screen and (min-width: 721px) and (max-width: 1120px) {
    .transaction-total{
        display: flex;
        p {
            display: none;
        }
        span {
            display: block;
            margin-top: 1rem;
            text-align: left !important;
        }
    }
    .transaction-table {
        display: block;

        .transaction-view {
            display: block;

            .white-space{
                width: unset;
                background-color: unset;
                top: 0px;
                position: relative;
                margin-left: 0rem;
            }
        }
        .fees-list {
            gap: 2.5rem !important;
        }
    }
}
</style>
